import {formValueSelector} from 'redux-form';
import deepEqual from 'deep-equal';
import {List, Set} from 'immutable';

import createSelector from 'web-app/util/custom-create-selector';
import * as SessionsSelectors from 'web-app/react/entities/sessions/selectors';
import Session from 'web-app/react/entities/sessions/model';
import generateUUID from 'web-app/util/uuid';
import {PricingType} from 'web-app/react/entities/common/pricing';
import {immutableSelectorCreator} from 'web-app/util/immutable';

import * as Constants from './constants';
import {convertGroupToAPI, getLatestValidFrom} from './helpers';

export const sessions = createSelector(
    state => state.plans.volumeSettings.groups.sessionVersions,
    SessionsSelectors.getSessionsState,
    (sessionVersionIds, sessions) => sessionVersionIds.map(version => sessions.get(version)),
);

const formValues = formValueSelector(Constants.FORM_NAME);
const groupValueSelector = state => formValues(state, 'groups');

const getSelectedSessions = createSelector(groupValueSelector, groups =>
    groups
        ? Set(
              groups.reduce(
                  (reduction, group) =>
                      reduction.concat(group && group.sessions ? group.sessions.map(session => session.sessionId) : []),
                  [],
              ),
          )
        : Set(),
);

const memoizedSessionIDs = immutableSelectorCreator(getSelectedSessions, sessionIDs => sessionIDs);

const sessionIdsFromStateToIgnore = createSelector(sessions, sessions =>
    sessions
        .filter(session => session.pricing.first().pricing === PricingType.HOURLY)
        .map(session => session.sessionId),
);

export const ignoreSessions = createSelector(
    memoizedSessionIDs,
    sessionIdsFromStateToIgnore,
    (selectedSessions, sessionsFromStateToIgnore) => List(selectedSessions).concat(sessionsFromStateToIgnore),
);

const sessionPricingForPricingGroup = (pricingGroupPricing, isPerSessionPriced) => {
    const maxVolume = pricingGroupPricing.map(p => p.volume).max();

    return List()
        .setSize(Math.max(maxVolume, Constants.NUMBER_OF_VOLUMES))
        .map((_, i) => {
            const pricingForIndex = pricingGroupPricing.find(pricing => pricing.volume === i + 1);
            if (pricingForIndex) {
                return {
                    // If the session is perSessionPriced (old way of pricing in volume pricing groups), we multiply the price by the volume
                    // such that saving the group will result in updating to the new way of doing volume pricing by total amount.
                    sessionPrice: `${
                        isPerSessionPriced ? pricingForIndex.price * pricingForIndex.volume : pricingForIndex.price
                    }`,
                    adhocPrice: pricingForIndex.adhocPrice ? `${pricingForIndex.adhocPrice}` : null,
                    volume: pricingForIndex.volume,
                };
            } else {
                return null;
            }
        });
};

const getFormPricingGroups = (pricing, allPricingGroups, isPerSessionPriced) =>
    pricing
        .groupBy(pricing => pricing.pricingGroupId)
        .map(agP => {
            const pricingGroupPricing = agP.sortBy(pricing => pricing.volume);

            const sessionPricing = sessionPricingForPricingGroup(pricingGroupPricing, isPerSessionPriced);

            const currentPricingGroup =
                allPricingGroups.length > 0
                    ? allPricingGroups.find(ag => ag.pricingGroupId === pricingGroupPricing.get(0).pricingGroupId)
                    : [];

            return {
                pricingGroupTitle: currentPricingGroup ? currentPricingGroup.title : '',
                pricingGroupLower: currentPricingGroup ? currentPricingGroup.lower : 0,
                pricingGroupId: pricingGroupPricing.first().pricingGroupId,
                pricing: pricingGroupPricing.first().pricing,
                sessionPricing: sessionPricing.toJS(),
            };
        });

// Checks whether this session is part of a volume pricing groups that works the old-fashions way, where the user had
// to enter a per-session price for a volume instead of a total price.
const isPricedPerSession = session =>
    session.pricing.find(p => p.volume !== 1) && session.sessionType === Session.SessionType.STANDALONE_SESSION;

export const discountGroups = createSelector(
    sessions,
    (_, props) => props.pricingGroups,
    (sessions, allPricingGroups) =>
        sessions
            .filter(session => session.volumeDiscountGroupId)
            .groupBy(session => session.volumeDiscountGroupId)
            .map((sessions, volumeDiscountGroupId) => {
                const firstSession = sessions.first();
                const isPerSessionPriced = isPricedPerSession(firstSession);

                const pricingGroups = getFormPricingGroups(firstSession.pricing, allPricingGroups, isPerSessionPriced);

                return {
                    volumeDiscountGroupId,
                    validFrom: getLatestValidFrom(sessions).format('YYYY-MM-DD'),
                    sessions: sessions.map(session => ({sessionId: session.sessionId})).toJS(),
                    pricingGroups: pricingGroups
                        .toList()
                        .sortBy(pricingGroup => pricingGroup.pricingGroupLower)
                        .toJS(),
                };
            })
            .toList()
            .toJS(),
);

export const perSessionPricingExists = createSelector(sessions, sessions => sessions.some(isPricedPerSession));

export const savableSessions = createSelector(
    discountGroups,
    (state, props) => props.discountVolumeGroups,
    sessions,
    perSessionPricingExists,
    (initialGroups, formGroups, sessions, perSessionPricingExists) => {
        const discountVolumeGroups = formGroups
            .map(g => {
                return convertGroupToAPI(g);
            })
            .filter(g => g);

        // We can easily detect that a session has been removed from a group if it is not in a group locally but has
        // a volumeDiscountGroupId
        const removedSessions = sessions.filter(
            s => s.volumeDiscountGroupId && !discountVolumeGroups.find(group => group.sessionIds.includes(s.sessionId)),
        );

        // Reset removed sessions back to being standalone
        const savableRemovedSessions = removedSessions
            .map(session => {
                const updatedSession = session.merge({
                    volumeDiscountGroupId: null,
                    sessionType: Session.SessionType.STANDALONE_SESSION,
                    pricing: session.pricing.filter(p => p.volume === 1),
                });

                return Session.toAPI(updatedSession);
            })
            .toArray();

        // Only update sessions in group if there has been an update
        const filteredFormGroups = perSessionPricingExists
            ? discountVolumeGroups
            : formGroups
                  .filter(group => {
                      const oldGroup = initialGroups.find(
                          iG => iG.volumeDiscountGroupId === group.volumeDiscountGroupId,
                      );
                      return !oldGroup || !deepEqual(group, oldGroup);
                  })
                  .map(g => {
                      return convertGroupToAPI(g);
                  })
                  .filter(g => g);

        // Sessions that should now be volume priced
        const savableVolumePricingSessions = filteredFormGroups.flatMap(volumePricingGroup => {
            // It is the job of the frontend to assign new groups a group ID
            const volumePricingGroupWithId = {
                ...volumePricingGroup,
                volumeDiscountGroupId: volumePricingGroup.volumeDiscountGroupId || generateUUID(),
            };

            return volumePricingGroupWithId.sessionIds.map(sid => {
                const session = sessions
                    .find(session => session.sessionId === sid)
                    .merge({
                        volumeDiscountGroupId: volumePricingGroupWithId.volumeDiscountGroupId,
                        sessionType: Session.SessionType.VOLUME_SESSION,
                    });

                return {
                    ...Session.toAPI(session),
                    validFrom: volumePricingGroupWithId.validFrom,
                    pricing: volumePricingGroup.pricing,
                };
            });
        });

        return savableVolumePricingSessions.concat(savableRemovedSessions);
    },
);

export const convertToStandalone = createSelector(
    (state, props) => props.groupToDelete,
    (state, props) => props.validFrom,
    sessions,
    (groupToDelete, validFrom, sessions) => {
        const newPricing = groupToDelete.pricing.filter(p => p.volume === 1);

        return groupToDelete.sessionIds.map(sid => {
            const session = sessions
                .find(session => session.sessionId === sid)
                .merge({
                    sessionType: Session.SessionType.STANDALONE_SESSION,
                    volumeDiscountGroupId: null,
                });

            return {
                ...Session.toAPI(session),
                validFrom,
                pricing: newPricing,
            };
        });
    },
);

export const deleteGroupInitialValues = createSelector(
    (state, props) => props.groupToDelete,
    sessions,
    (groupToDelete, sessions) => {
        if (!groupToDelete) {
            return null;
        }

        const date = groupToDelete.sessionIds
            .map(sid => {
                const session = sessions.find(session => session.sessionId === sid);
                return session.validFrom.clone().add(1, 'day');
            })
            .reduce((last, current) => (last && last.isAfter(current) ? last : current), null);

        return {
            date: date.format('YYYY-MM-DD'),
        };
    },
);
